import React from 'react';
import { EventVenueDialog } from '../EventVenueDialog';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useAutoPopulation } from '@shared/utils/accommodations';
import { AccommodationHotelsDocument, PageContainerBySlugDocument } from '@graphql/generated';
import { useApolloClient } from '@apollo/client';
import { useUpdateWebsitePreview } from '@apps/admin/routes/WebsiteEditor/hooks/useUpdateWebsitePreview';
import { useVenueInfo } from '../VenueInfo';

interface EventVenueProps
  extends Readonly<{
    pageSlug: string;
  }> {}
/**
 * EventVenue component is a wrapper around EventVenueDialog component.
 */
export const EventVenue = ({ pageSlug }: EventVenueProps) => {
  const client = useApolloClient();
  const { eventInfo } = useEventInfo();

  const venueInfoProps = useVenueInfo();
  const { venueInfo, eventVenueState, isSuperAdmin, isOpen: isVenueDialogOpen, onClose: handleVenueDialogClose } = venueInfoProps;

  const { updateWebsitePreview } = useUpdateWebsitePreview();

  const { startAutoPopulation } = useAutoPopulation({
    mode: 'auto',
    context: 'EventVenue',
    // On completion, refetch accommodations data to update the UI (this is only called once).
    onComplete: () => {
      client
        .refetchQueries({
          include: [PageContainerBySlugDocument, AccommodationHotelsDocument],
        })
        .then(() => updateWebsitePreview());
    },
  });

  const handleOnSaveVenue = useEventCallback(async () => {
    const eventId = eventInfo?.eventId;

    if (!eventId || !venueInfo?.latitude || !venueInfo?.longitude) {
      return;
    }

    await startAutoPopulation({
      venue: { latitude: venueInfo.latitude, longitude: venueInfo.longitude, googlePlaceId: venueInfo.googlePlaceId },
      eventId,
    });
  });

  return (
    <EventVenueDialog
      eventVenueState={eventVenueState}
      isOpen={isVenueDialogOpen()}
      onClose={handleVenueDialogClose}
      pageSlug={pageSlug}
      isSuperAdmin={isSuperAdmin}
      onSaveVenue={handleOnSaveVenue}
    />
  );
};
